import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CustomBreadcrumb, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { Home, Globe, School as SchoolIcon, Mail, Phone, Calendar, ArrowLeft, Users, AlertCircle, UserCog, GraduationCap, Heart, Shield } from 'lucide-react';
import { schoolApi } from '../api/schoolApi';
import { School, SchoolUserCount } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';
import { getMediaUrl } from '@/shared/utils/media';

const ROLE_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
    SUPER_ADMIN: { label: 'Super Admins', color: '#6366f1', icon: <Shield size={16} /> },
    SCHOOL_ADMIN: { label: 'School Admins', color: '#0ea5e9', icon: <UserCog size={16} /> },
    EMPLOYEE: { label: 'Employees', color: '#10b981', icon: <UserCog size={16} /> },
    PARENT: { label: 'Parents', color: '#f59e0b', icon: <Heart size={16} /> },
    STUDENT: { label: 'Students', color: '#ec4899', icon: <GraduationCap size={16} /> },
};

const getRoleMeta = (role: string) => {
    const upper = role.toUpperCase();
    return ROLE_META[upper] || { label: role, color: '#64748b', icon: <Users size={16} /> };
};

export const SchoolDetails = () => {
    const { schoolSlug } = useParams<{ schoolSlug: string }>();
    const navigate = useNavigate();
    const { showToast } = useToastStore();

    const [school, setSchool] = useState<School | null>(null);
    const [userCounts, setUserCounts] = useState<SchoolUserCount[]>([]);
    const [totalUsers, setTotalUsers] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!schoolSlug) return;
            setIsLoading(true);
            setError(null);
            try {
                const [schoolRes, usersRes] = await Promise.all([
                    schoolApi.getSchoolBySlug(schoolSlug),
                    schoolApi.getSchoolUsers(schoolSlug),
                ]);

                if (schoolRes.success && schoolRes.data) {
                    setSchool(schoolRes.data as any);
                } else {
                    setError(schoolRes.error || 'Failed to load school details');
                }

                if (usersRes.success && usersRes.data) {
                    setUserCounts(usersRes.data.counts || []);
                    setTotalUsers(usersRes.data.total_count || 0);
                }
            } catch (err) {
                setError('Unable to connect to the server.');
                showToast('Failed to load school details', 'error');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [schoolSlug]);

    const ShimmerBlock = ({ w, h, r = '8px' }: { w: string; h: string; r?: string }) => (
        <div style={{
            width: w, height: h, borderRadius: r,
            background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
            backgroundSize: '800px 100%',
            animation: 'pulse 1.5s ease-in-out infinite',
        }} />
    );

    const logoUrl = getMediaUrl(school?.logo_url);
    const bannerUrl = getMediaUrl(school?.banner_url);

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Manage Schools', path: '/super-admin/tenants', icon: <Globe size={14} /> },
                    { label: school?.name || 'School Details', icon: <SchoolIcon size={14} /> },
                ]}
            />

            <CustomCommonScreenHeader
                title={school?.name || 'School Details'}
                subtitle={school?.tagline || 'Tenant overview and user statistics.'}
                icon={<SchoolIcon size={24} />}
                actionLabel="Back"
                actionIcon={<ArrowLeft size={18} />}
                onActionClick={() => navigate('/super-admin/tenants')}
            />

            {error && (
                <div style={{
                    marginTop: '1.5rem', padding: '1rem 1.25rem', borderRadius: '12px',
                    background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)',
                    display: 'flex', alignItems: 'center', gap: '0.75rem'
                }}>
                    <AlertCircle size={20} color="#ef4444" />
                    <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Failed to load school</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{error}</div>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <ShimmerBlock w="100%" h="180px" r="14px" />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                        {[1, 2, 3, 4].map(i => <ShimmerBlock key={i} w="100%" h="100px" r="12px" />)}
                    </div>
                </div>
            ) : school ? (
                <>
                    {/* Hero Card with Banner & Logo */}
                    <div style={{
                        marginTop: '1.5rem', borderRadius: '16px', overflow: 'hidden',
                        border: '1px solid var(--border-color)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                        background: 'var(--card-bg)',
                    }}>
                        {/* Banner */}
                        <div style={{
                            height: '160px', position: 'relative',
                            background: bannerUrl
                                ? `url(${bannerUrl}) center/cover`
                                : `linear-gradient(135deg, ${school.primary_color || '#6366f1'}, ${school.accent_color || '#818cf8'})`,
                        }}>
                            <div style={{
                                position: 'absolute', top: '12px', right: '12px',
                                padding: '0.3rem 0.7rem', borderRadius: '20px',
                                background: 'rgba(16,185,129,0.95)', color: 'white',
                                fontSize: '0.7rem', fontWeight: 700,
                            }}>Active</div>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '1.25rem 1.5rem 1.5rem', position: 'relative' }}>
                            <div style={{
                                width: '80px', height: '80px', borderRadius: '16px',
                                background: logoUrl ? 'white' : (school.primary_color || '#6366f1'),
                                border: '4px solid var(--card-bg)',
                                position: 'absolute', top: '-44px', left: '1.5rem',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 6px 16px rgba(0,0,0,0.12)', overflow: 'hidden'
                            }}>
                                {logoUrl ? (
                                    <img src={logoUrl} alt={school.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                ) : (
                                    <SchoolIcon size={36} color="white" />
                                )}
                            </div>

                            <div style={{ paddingLeft: '6rem', paddingTop: '0.5rem' }}>
                                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)' }}>{school.name}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', fontWeight: 600, marginTop: '0.15rem' }}>/{school.slug}</div>
                                {school.tagline && (
                                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.4rem', fontStyle: 'italic' }}>{school.tagline}</div>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '1.25rem', paddingLeft: '6rem' }}>
                                {school.support_email && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                        <Mail size={14} color="var(--accent-primary)" /> {school.support_email}
                                    </div>
                                )}
                                {school.support_phone && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                        <Phone size={14} color="var(--accent-primary)" /> {school.support_phone}
                                    </div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                    <Calendar size={14} color="var(--accent-primary)" /> Joined {new Date(school.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* User Counts Section */}
                    <div style={{ marginTop: '2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Users size={18} color="var(--accent-primary)" /> User Statistics
                            </h2>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                Total: {totalUsers}
                            </span>
                        </div>

                        {userCounts.length === 0 ? (
                            <div style={{
                                padding: '2rem', textAlign: 'center',
                                background: 'var(--card-bg)', borderRadius: '12px',
                                border: '1px dashed var(--border-color)', color: 'var(--text-muted)',
                                fontSize: '0.85rem'
                            }}>
                                No user data available.
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.85rem' }}>
                                {userCounts.map(uc => {
                                    const meta = getRoleMeta(uc.role);
                                    return (
                                        <div key={uc.role} style={{
                                            background: 'var(--card-bg)', borderRadius: '12px',
                                            border: '1px solid var(--border-color)', padding: '1rem 1.1rem',
                                            borderLeft: `4px solid ${meta.color}`,
                                            display: 'flex', alignItems: 'center', gap: '0.85rem',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                        }}>
                                            <div style={{
                                                width: '40px', height: '40px', borderRadius: '10px',
                                                background: `${meta.color}15`, color: meta.color,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                {meta.icon}
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{meta.label}</div>
                                                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.1 }}>{uc.count}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Branding & Identity Section removed */}
                </>
            ) : null}
        </div>
    );
};
